import { fabric } from 'fabric'
import type { FrameSize } from '../frame'
import WbFrame, { frameSizes } from '../frame'
import WbArticleImage from '../articleImage'
import WbImage from '../image'
import WbModelDetails from '../modelDetails'
import type { IWhiteboardTemplate, IWhiteboardTemplateFileMapping, IWhiteboardTemplateOption } from './IWhiteboardTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, whiteboardConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import utils from '@/services/utils'
import graphicTeeTechSheetImage from '@/assets/templateLayoutImages/graphicTeeTechSheet.png'
import type LinkedCustomer from '@/models/linkedCustomer'

class GraphicTeeTechSheet implements IWhiteboardTemplate {
  id = whiteboardConstants.frameTemplatesId.graphicTeeTechSheet
  name = 'Graphic Tee Tech Sheet'
  layoutImage = graphicTeeTechSheetImage
  settings = {
    articleImage: { width: 500, height: 500, scale: whiteboardConstants.frameImageDefaultScaleFactors.large },
    articleDetails: { fontFamily: 'Neue Plak', fontSize: 12, fontWeight: 'bold', fill: '#ffffff', textAlign: 'left', showLabels: false },
    modelDetails: { width: 325, fontFamily: 'Neue Plak', fontSize: 12, fontWeight: 'bold', fill: '#ffffff', textAlign: 'left', attributes: ['ModelNumber', '_RetailPrice'], attributesSeparator: ' // ', attributePlaceHolder: '[Blank]', showLabels: false },
    textBox: { fontFamily: 'Neue Plak' },
  }

  imgSize: number = 300
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  frameWidth: number = frameSizes.wide.width
  frameHeight: number = frameSizes.wide.height
  logoWidth = 155
  logoHeight = 87
  logoScaling = 20
  uaNameLogoWidth = 400
  uaNameLogoHeight = 38
  uaNameLogoScaling = 40
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  frameSpacing = 50
  leftPadding: number = 50
  rightPadding: number = 30
  textObjectsMaxWidth = 200
  articleImageProps = {
    bottomPadding: 50,
  }

  modelNameProps = {
    bottomPadding: 30,
    fontSize: 34,
    width: 325,
  }

  modelDetailsProps = {
    padding: 17,
  }

  headerImageNameLog = {
    bottomPadding: 180,
  }

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, defaultImageTypeForTemplates: string): IWhiteboardTemplateOption[] {
    const options: IWhiteboardTemplateOption[] = []

    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: defaultImageTypeForTemplates,
      data: whiteboardConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })

    return options
  }

  getFileMapping(): IWhiteboardTemplateFileMapping[] {
    return [{
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    }]
  }

  async generate(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articlesData: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, startPosition: IPoint, direction: 'horizontal' | 'vertical', excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    this.frameSpacing = (direction === 'vertical') ? 100 : 50
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, startPosition, direction, retailPg, wholesalePg, outletPg, isExporting)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, startPosition: IPoint, direction: 'horizontal' | 'vertical', retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting: boolean = false): Promise<IWbObject[]> {
    const objects: IWbObject[] = []
    this.imgSize = (this.settings.articleImage.width * this.settings.articleImage.scale) / 100
    this.startPositionX = isExporting ? 0 : startPosition.x ? startPosition.x : 0
    this.startPositionY = isExporting ? 0 : startPosition.y ? startPosition.y : 0
    const frameSize = 'wide' // options.frameSize
    this.changePropsAsPerFrameSize(frameSize)

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    let frameLeft = this.startPositionX
    let frameTop = this.startPositionY
    let frameCount = 0

    for (const firstArticle of uniqueModelArticles) {
      const frameObjects: IWbObject[] = []

      frameObjects.push(await this.createHeaderLogo(
        frameTop + this.leftPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      frameObjects.push(await this.createHeaderNameLogo(
        frameTop + this.frameHeight - this.headerImageNameLog.bottomPadding,
        frameLeft + this.leftPadding,
        isExporting,
      ))

      const imgLeft = frameLeft + this.frameWidth - this.imgSize - this.rightPadding
      const imgTop = frameTop + this.frameHeight - this.imgSize - this.articleImageProps.bottomPadding
      frameObjects.push(await this.createImage(
        imgTop,
        imgLeft,
        firstArticle,
        this.settings.articleImage.scale,
        options.imageType,
        isExporting,
      ))

      const modelNameOpt = {
        left: frameLeft + this.frameWidth - this.modelNameProps.width,
        top: frameTop + this.frameHeight / 2,
        showLabels: this.settings.modelDetails.showLabels,
        width: this.modelNameProps.width,
        attributes: ['ModelName'],
        attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
        fontFamily: this.settings.modelDetails.fontFamily,
        fontSize: this.modelNameProps.fontSize,
        fontWeight: this.settings.modelDetails.fontWeight,
        textAlign: this.settings.modelDetails.textAlign,
        fill: this.settings.modelDetails.fill,
      }
      const modelNameObject = await WbModelDetails.loadModelDetails(firstArticle, modelNameOpt)
      const modelNameHeight = this.getTextRequiredHeightBasedOnWidth(firstArticle.ModelName, this.modelNameProps.width, this.settings.modelDetails.fontFamily, this.settings.modelDetails.fontSize, this.settings.modelDetails.fontWeight)
      const modelNameTop = frameTop + this.frameHeight / 2 - modelNameHeight - this.modelNameProps.bottomPadding
      modelNameObject.top = modelNameTop
      frameObjects.push(isExporting ? modelNameObject.toObject() as WbModelDetails : modelNameObject)

      const modelDetailsTop = (modelNameObject && modelNameObject.top && modelNameObject.height ? modelNameObject.top + modelNameObject.height : frameTop + this.frameHeight / 2) + this.modelDetailsProps.padding
      const modelDetailsOpt = {
        left: frameLeft + this.frameWidth - this.settings.modelDetails.width,
        top: modelDetailsTop,
        showLabels: this.settings.modelDetails.showLabels,
        width: this.settings.modelDetails.width,
        attributes: this.settings.modelDetails.attributes,
        attributesSeparator: this.settings.modelDetails.attributesSeparator,
        attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
        fontFamily: this.settings.modelDetails.fontFamily,
        fontSize: this.settings.modelDetails.fontSize,
        fontWeight: this.settings.modelDetails.fontWeight,
        textAlign: this.settings.modelDetails.textAlign,
        fill: this.settings.modelDetails.fill,
      }
      const modelDetailsObject = await WbModelDetails.loadModelDetails(firstArticle, modelDetailsOpt)
      frameObjects.push(isExporting ? modelDetailsObject.toObject() as WbModelDetails : modelDetailsObject)

      objects.push(...frameObjects)
      objects.push(new WbFrame(
        frameSize,
        {
          children: frameObjects.map(obj => obj.id || ''),
          name: firstArticle.ModelName,
          left: frameLeft,
          top: frameTop,
          sortOrder: frameCount,
          lock: false,
          fill: '#000000',
        },
      ))

      if (direction === 'vertical') {
        frameTop += isExporting ? 0 : this.frameHeight + this.frameSpacing
      }
      else {
        frameLeft += isExporting ? 0 : this.frameWidth + this.frameSpacing
      }
      frameCount++
    }

    return objects
  }

  changePropsAsPerFrameSize(frameSize: FrameSize) {
    this.frameWidth = frameSizes[frameSize].width
    this.frameHeight = frameSizes[frameSize].height
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  async createHeaderNameLogo(top: number, left: number, isExporting: boolean = false) {
    const options = {
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
      scaleX: this.uaNameLogoScaling / 100,
      scaleY: this.uaNameLogoScaling / 100,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaNameLogoWhite, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaNameLogoWhite, options)
    }
  }

  async createHeaderLogo(top: number, left: number, isExporting: boolean = false) {
    const options = {
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
      scaleX: this.logoScaling / 100,
      scaleY: this.logoScaling / 100,
    }
    if (isExporting) {
      return await WbImage.getJsonObject(appConstants.logoPaths.uaIconWhite, options) as WbImage
    }
    else {
      return await WbImage.loadFromUrl(appConstants.logoPaths.uaIconWhite, options)
    }
  }

  async createImage(top: number, left: number, article: MyArticle, imageScaleFactor: number, imageType: string, isExporting: boolean) {
    const opt = {
      top,
      left,
      catalogCode: article.CatalogCode,
      articleId: article.Id,
      objectId: article.CatalogArticleId,
      isRequest: article._IsRequestArticle,
      assetKey: imageType,
      scaleX: imageScaleFactor / 100,
      scaleY: imageScaleFactor / 100,
    }
    if (isExporting) {
      return await WbArticleImage.getJsonObject(article, this.settings.articleImage.width, this.settings.articleImage.height, opt)
    }
    else {
      return await WbArticleImage.loadArticleImage(article, this.settings.articleImage.width, this.settings.articleImage.height, opt)
    }
  }
}

export default new GraphicTeeTechSheet()
